import type { SearchSourceType } from '@affine/core/modules/docs-search';
import {
  getBackendAvailabilityUiSnapshot,
  isBackendAvailableForImmediateRequest,
  subscribeBackendAvailabilityUi,
  subscribeBackendRecovery,
} from '@affine/core/utils/backend-availability';
import {
  type PdfPageSearchQuery,
  pdfPageSearchQuery,
  ServerFeature,
} from '@affine/graphql';
import type { I18nString } from '@affine/i18n';
import type { IndexerPreferOptions } from '@affine/nbstore';
import {
  type Effect,
  effect,
  Entity,
  LiveData,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { truncate } from 'lodash-es';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  from,
  map,
  merge,
  type Observable,
  of,
  shareReplay,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';

import type { WorkspaceServerService } from '../../cloud';
import type { DocsService } from '../../doc';
import type { DocDisplayMetaService } from '../../doc-display-meta';
import type {
  DocsSearchService,
  SearchPartitionSyncService,
  WarmCacheService,
} from '../../docs-search';
import {
  focusMarkedText,
  focusQueryTerms,
} from '../../docs-search/services/query-terms';
import type { FeatureFlagService } from '../../feature-flag';
import type { WorkspaceService } from '../../workspace';
import type {
  QuickSearchFooter,
  QuickSearchSession,
} from '../providers/quick-search-provider';
import {
  QUICK_SEARCH_HIT_KIND_LABEL,
  type QuickSearchHitVisual,
  type QuickSearchItem,
} from '../types/item';
import {
  type DocsPayload,
  type DocsSearchResult,
  type PdfPageSearchHit,
} from './docs-pdf-search';
import {
  buildSearchDocBuckets,
  buildSearchDocPlaces,
  docPlacesListKey,
  markedDocTitle,
  PLACES_PER_DOC,
  type SearchDocBucket,
  type SearchDocPlace,
  searchDocPlaceSourceTypes,
  splitForLoadMore,
} from './docs-search-grouping';
import {
  DOCS_SEARCH_TRUNCATED_STATUS,
  type DocsSearchModeFlags,
  resolvePreferredDocsSearchMode,
  SEARCH_INDEX_UNAVAILABLE_STATUS,
  statusForDocsSearchMode,
} from './docs-search-status';
import {
  createDedupedStaleResultNudge,
  type DedupedStaleResultNudge,
  filterStaleLoadedBlockResults,
} from './docs-stale-results';

type DocsResultGroup = NonNullable<QuickSearchItem['group']>;

const PDF_QUICK_SEARCH_LIMIT = 20;
const PDF_SEARCH_FAILURE_COOLDOWN_MS = 30_000;
const DOCS_SEARCH_DEBOUNCE_MS = 150;
/**
 * Below this many exact local pages, similar pages are listed under them
 * (user card dec-search-lf-defaults-1020, A).
 */
export const SIMILAR_FALLBACK_BELOW = 3;

/** What the server found beyond the local answer. */
export type OnlineHits = {
  docs: DocsSearchResult[];
  pdfHits: PdfPageSearchHit[];
  /** Page rows the button adds below; the button names this number. */
  count: number;
  /** Similar hits: the server found nothing exact beyond the local answer. */
  similar: boolean;
};

const pageCount = (docs: readonly DocsSearchResult[]) =>
  new Set(docs.map(doc => doc.docId)).size;

/**
 * The server's hits beyond the listed local answer. `remote` holds only the
 * row-shard hits the server answers (DocsSearchService.mergeDatabaseSearch);
 * those stay even on a listed page, because that page's row shard is not in
 * the local answer. Every other hit of a listed page drops out: a page has one
 * source. The local rows themselves never change.
 */
export function onlineHitsBeyond(
  local: readonly DocsSearchResult[],
  remote: readonly DocsSearchResult[],
  pdfHits: readonly PdfPageSearchHit[]
): Pick<OnlineHits, 'docs' | 'pdfHits'> {
  const listed = new Set(local.map(doc => doc.docId));
  const docs = remote.filter(
    doc => !!doc.databaseRowShardDocId || !listed.has(doc.docId)
  );
  const pages = pdfHits.filter(hit => !listed.has(hit.docId));
  return { docs, pdfHits: pages };
}

/** The online part of a cloud search, as the footer shows it. */
export type OnlineSearchState =
  | { kind: 'none' }
  | { kind: 'counting' }
  | { kind: 'ready'; count: number; similar: boolean }
  | { kind: 'shown' }
  | { kind: 'offline' }
  | { kind: 'unreachable' };

/**
 * Whether a web search shows the desktop hint: the server holds hits this
 * device lacks, or it cannot be reached.
 */
export function showsDesktopHint(
  state: OnlineSearchState,
  backendAvailable: boolean,
  isElectron: boolean
) {
  if (isElectron || state.kind === 'none') return false;
  return (
    (state.kind === 'ready' && state.count > 0) ||
    state.kind === 'offline' ||
    state.kind === 'unreachable' ||
    !backendAvailable
  );
}

export function onlineSearchFooter(
  state: OnlineSearchState,
  show: () => void
): QuickSearchFooter | null {
  switch (state.kind) {
    case 'counting':
      return {
        label: { i18nKey: 'com.streamlain.quicksearch.online.counting' },
        busy: true,
      };
    case 'ready':
      return state.count > 0
        ? {
            label: {
              i18nKey: state.similar
                ? 'com.streamlain.quicksearch.online.similar'
                : 'com.streamlain.quicksearch.online.more',
              options: { count: state.count },
            },
            action: show,
          }
        : null;
    case 'offline':
      return {
        label: { i18nKey: 'com.streamlain.quicksearch.online.offline' },
      };
    case 'unreachable':
      return {
        label: { i18nKey: 'com.streamlain.quicksearch.online.unreachable' },
      };
    default:
      return null;
  }
}

export function filterIncompleteCloudDatabaseRowHits(
  results: DocsSearchResult[]
): DocsSearchResult[] {
  return results.filter(
    result =>
      result.sourceType !== 'database' ||
      !result.databaseRowId ||
      !!result.databaseRowShardDocId
  );
}

// Quick Search renders the snippet on one ellipsized line. Keep the first
// matched term near the left edge so its highlight stays inside the row.
const VISIBLE_SNIPPET_WINDOW = { before: 24, after: 72 };

export function createVisibleQuerySnippet(
  content: string | undefined,
  query: string
) {
  if (!content || !query.trim()) return content;

  if (content.includes('<b>')) {
    return focusMarkedText(content, VISIBLE_SNIPPET_WINDOW) ?? content;
  }

  return focusQueryTerms(content, query, VISIBLE_SNIPPET_WINDOW) ?? content;
}

const STICKY_NOTE_FLAVOUR = 'affine:sticky-note';

/** The miniature of one hit; a sticky note is a text hit of its own flavour. */
export function docsHitVisual(row: {
  sourceType: SearchSourceType;
  flavour?: string;
  blobId?: string;
}): QuickSearchHitVisual {
  if (row.flavour === STICKY_NOTE_FLAVOUR) return { kind: 'sticky' };
  switch (row.sourceType) {
    case 'image':
      return row.blobId
        ? { kind: 'image', blobId: row.blobId }
        : { kind: 'image' };
    case 'edgeless':
      return { kind: 'canvas' };
    case 'embed':
      return { kind: 'link' };
    default:
      return { kind: row.sourceType };
  }
}

export class DocsQuickSearchSession
  extends Entity
  implements QuickSearchSession<'docs', DocsPayload>
{
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly docsSearchService: DocsSearchService,
    private readonly docsService: DocsService,
    private readonly docDisplayMetaService: DocDisplayMetaService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly searchPartitionSync: SearchPartitionSyncService,
    private readonly warmCache: WarmCacheService
  ) {
    super();
    if (this.isCloudWorkspace)
      this.disposables.push(
        subscribeBackendAvailabilityUi(() => {
          const available = getBackendAvailabilityUiSnapshot();
          this.backendAvailableUi$.next(available);
          const state = this.onlineState$.value.kind;
          if (!available && (state === 'counting' || state === 'ready')) {
            this.onlineState$.next({ kind: 'offline' });
          }
        }),
        subscribeBackendRecovery(() => {
          const state = this.onlineState$.value.kind;
          if (state === 'offline' || state === 'unreachable') {
            this.retryOnline$.next();
          }
        })
      );
    this.staleResultNudge = createDedupedStaleResultNudge({
      addPriority: (docId, priority) =>
        this.docsSearchService.indexer.addPriority(docId, priority),
      warn: result => {
        console.warn(
          '[DocsQuickSearchSession] Filtering stale search result for missing loaded-doc block',
          result
        );
      },
    });
  }

  private readonly isSupportServerIndexer = () =>
    this.workspaceServerService.server?.config$.value.features.includes(
      ServerFeature.Indexer
    ) ?? false;

  private readonly isEnableBatterySaveMode = () =>
    this.featureFlagService.flags.enable_battery_save_mode.value;

  private readonly isIndexerLoading$ = this.docsSearchService.indexerState$.map(
    ({ completed }) => {
      return !completed;
    }
  );

  private readonly isQueryLoading$ = new LiveData(false);

  private readonly onlineState$ = new LiveData<OnlineSearchState>({
    kind: 'none',
  });

  private readonly backendAvailableUi$ = new LiveData(
    getBackendAvailabilityUiSnapshot()
  );
  private readonly retryOnline$ = new Subject<void>();

  /** Request failures show at once; other outages share the app's UI grace. */
  readonly footer$ = LiveData.computed(get => {
    const state = get(this.onlineState$);
    if (state.kind === 'none') return null;
    if (state.kind === 'unreachable' || !get(this.backendAvailableUi$)) {
      return onlineSearchFooter(this.unavailableOnlineState(), this.showOnline);
    }
    return state.kind === 'offline'
      ? null
      : onlineSearchFooter(state, this.showOnline);
  });

  /**
   * On the web, while the server holds hits this device lacks or cannot be
   * reached: the desktop app keeps more of the workspace searchable locally.
   */
  readonly desktopHint$ = LiveData.computed(
    get =>
      this.isCloudWorkspace &&
      showsDesktopHint(
        get(this.onlineState$),
        get(this.backendAvailableUi$),
        BUILD_CONFIG.isElectron
      )
  );

  /** True once the local answer of the current query is listed. */
  readonly isSettled$ = new LiveData(true);

  isCloudWorkspace = this.workspaceService.workspace.flavour !== 'local';

  isLoading$ = LiveData.computed(get => {
    return (
      (this.isCloudWorkspace ? false : get(this.isIndexerLoading$)) ||
      get(this.isQueryLoading$)
    );
  });

  error$ = new LiveData<any>(null);

  private readonly modeStatus$ = new LiveData<I18nString | null>(null);

  /** Listed docs whose text is not on this device while offline. */
  private readonly offlineMissing$ = new LiveData(0);

  status$ = LiveData.computed<I18nString | null>(get => {
    if (get(this.docsSearchService.indexUnavailable$)) {
      return SEARCH_INDEX_UNAVAILABLE_STATUS;
    }
    const offlineMissing = get(this.offlineMissing$);
    if (offlineMissing > 0) {
      return {
        i18nKey: 'com.streamlain.quicksearch.status.offline-missing',
        options: { count: offlineMissing },
      };
    }
    const modeStatus = get(this.modeStatus$);
    if (modeStatus) return modeStatus;
    const expiredDocs = get(this.docsSearchService.expiredDocCount$);
    if (expiredDocs > 0) {
      return {
        i18nKey: 'com.streamlain.quicksearch.status.grants-expired',
        options: { count: expiredDocs },
      };
    }
    return get(this.docsSearchService.localTruncated$)
      ? DOCS_SEARCH_TRUNCATED_STATUS
      : null;
  });

  /** Docs made available offline in this session; they load once online. */
  private readonly keptWhileOffline = new Set<string>();

  /**
   * Offline, a doc without full text on this device is listed greyed and does
   * not open; it can be made available offline instead.
   */
  private readonly offlineState = (
    docId: string
  ): QuickSearchItem['offline'] => {
    if (!this.isCloudWorkspace || isBackendAvailableForImmediateRequest())
      return undefined;
    if (this.keptWhileOffline.has(docId)) return 'pending';
    return this.warmCache.includes(docId) ? undefined : 'unavailable';
  };

  private readonly keepOffline = (docId: string) => {
    this.warmCache.keepOffline(docId);
    this.keptWhileOffline.add(docId);
    this.publishItems();
  };

  lastQuery = '';

  items$ = new LiveData<QuickSearchItem<'docs', DocsPayload>[]>([]);

  private pdfSearchFailedUntil = 0;

  /** Browser connectivity chooses the message, never whether to ask the server. */
  private readonly unavailableOnlineState = (): OnlineSearchState => ({
    kind:
      typeof navigator !== 'undefined' && !navigator.onLine
        ? 'offline'
        : 'unreachable',
  });

  private readonly isPdfSearchInCooldown = () =>
    this.pdfSearchFailedUntil > Date.now();

  private readonly staleResultNudge: DedupedStaleResultNudge;

  private readonly modeFlags = (): DocsSearchModeFlags => ({
    isCloudWorkspace: this.isCloudWorkspace,
    isBackendAvailable: isBackendAvailableForImmediateRequest(),
    isServerIndexerSupported: this.isSupportServerIndexer(),
    isBatterySaveMode: this.isEnableBatterySaveMode(),
    isPdfSearchInCooldown: this.isPdfSearchInCooldown(),
  });

  private readonly resolveDocsSearchPrefer = (): IndexerPreferOptions =>
    resolvePreferredDocsSearchMode(this.modeFlags());

  private readonly statusForSearchMode = (
    prefer: IndexerPreferOptions
  ): I18nString | null => statusForDocsSearchMode(this.modeFlags(), prefer);

  private readonly canSearchPdfPages = (prefer: IndexerPreferOptions) =>
    prefer === 'remote' &&
    this.isSupportServerIndexer() &&
    !this.isEnableBatterySaveMode() &&
    !this.isPdfSearchInCooldown();

  private readonly searchPdfPages = async (
    query: string,
    prefer: IndexerPreferOptions
  ): Promise<PdfPageSearchHit[]> => {
    if (
      !isBackendAvailableForImmediateRequest() ||
      !this.canSearchPdfPages(prefer)
    )
      return [];

    const server = this.workspaceServerService.server;
    if (!server) return [];

    const result: PdfPageSearchQuery = await server.gql({
      query: pdfPageSearchQuery,
      variables: {
        id: this.workspaceService.workspace.id,
        input: {
          keyword: query,
          limit: PDF_QUICK_SEARCH_LIMIT,
        },
      },
    });

    return result.workspace.pdfSearch;
  };

  private readonly createGroup = (query: string) => ({
    id: 'docs',
    label: {
      i18nKey: 'com.streamlain.quicksearch.group.searchfor',
      options: { query: truncate(query) },
    },
    score: 5,
  });

  // Server-only hits, listed below the local ones once the user asks for them.
  private readonly onlineGroup = {
    id: 'docs-online',
    label: { i18nKey: 'com.streamlain.quicksearch.group.online' },
    score: 2,
  };

  // Pages similar to the query, right below the few exact local ones.
  private readonly similarGroup = {
    id: 'docs-similar',
    label: { i18nKey: 'com.streamlain.quicksearch.group.similar' },
    score: 4,
  };

  // Similar server hits, offered when the server found nothing exact more.
  private readonly similarOnlineGroup = {
    id: 'docs-similar-online',
    label: { i18nKey: 'com.streamlain.quicksearch.group.similarOnline' },
    score: 2,
  };

  // Trashed docs stay searchable, under their own heading after live hits.
  private readonly trashGroup = {
    id: 'docs-trash',
    label: { i18nKey: 'com.streamlain.quicksearch.group.trash' },
    score: 1,
  };

  private readonly getDocRecord = (docId: string) => {
    const docRecord = this.docsService.list.doc$(docId).value;
    return docRecord ?? null;
  };

  private readonly createItems = (
    query: string,
    docs: DocsSearchResult[],
    pdfHits: PdfPageSearchHit[],
    group: DocsResultGroup = this.createGroup(query)
  ) => {
    // Keep the place counts and the listed places on the same filtered
    // local/remote set. Otherwise stale backend rows inflate a doc's count but
    // never appear under it.
    const displayDocs = filterStaleLoadedBlockResults(query, docs, {
      onStaleResult: this.staleResultNudge.nudge,
      resolveBlockStatus: this.resolveBlockStatusForList,
    });
    const items = buildSearchDocBuckets(displayDocs, pdfHits).flatMap(
      bucket => {
        if (!this.getDocRecord(bucket.docId)?.trash$.value) {
          return this.createDocItems(query, bucket, group);
        }
        if (
          group.id === this.similarGroup.id ||
          group.id === this.similarOnlineGroup.id
        ) {
          return [];
        }
        return this.createDocItems(query, bucket, this.trashGroup).map(
          item => ({
            ...item,
            trashed: true,
          })
        );
      }
    );
    // The offline count describes the exact local answer only.
    if (group.id === 'docs') {
      this.offlineMissing$.next(
        items.filter(
          item => item.row === 'doc' && item.offline === 'unavailable'
        ).length
      );
    }
    return items;
  };

  /**
   * A doc that cannot be opened offline: the greyed doc row (it does not
   * open), and while not yet kept the row that makes it available offline.
   */
  private readonly createOfflineDocItems = (
    query: string,
    bucket: SearchDocBucket,
    group: DocsResultGroup,
    doc: {
      title: string;
      icon: QuickSearchItem['icon'];
      updatedDate: number | undefined;
      offline: NonNullable<QuickSearchItem['offline']>;
    }
  ): QuickSearchItem<'docs', DocsPayload>[] => {
    const payload = {
      docId: bucket.docId,
      title: doc.title,
      sourceTypes: bucket.sourceTypes,
    };
    const items: QuickSearchItem<'docs', DocsPayload>[] = [
      {
        id: `doc-group:${bucket.docId}:${query}`,
        source: 'docs',
        group,
        label: {
          title: preferHighlightedTitle(markedDocTitle(bucket), doc.title),
        },
        score: bucket.score,
        icon: doc.icon,
        row: 'doc',
        timestamp: doc.updatedDate,
        offline: doc.offline,
        payload,
        beforeSubmit: () => false,
      },
    ];
    if (doc.offline === 'unavailable') {
      items.push({
        id: `doc-keep-offline:${bucket.docId}:${query}`,
        source: 'docs',
        group,
        label: { i18nKey: 'com.streamlain.quicksearch.offline.keep' },
        score: 0,
        row: 'more',
        payload,
        beforeSubmit: () => {
          this.keepOffline(bucket.docId);
          return false;
        },
      });
    }
    return items;
  };

  /**
   * A doc's result group: the doc row (it opens the doc), then the places
   * where the query was found, always in view — the best PLACES_PER_DOC, the
   * rest behind "Show N more".
   */
  private readonly createDocItems = (
    query: string,
    bucket: SearchDocBucket,
    group: DocsResultGroup
  ) => {
    const docRecord = this.getDocRecord(bucket.docId);
    if (!docRecord) return [];
    const { title, icon, updatedDate } =
      this.docDisplayMetaService.getDocDisplayMeta(docRecord);
    // Scoped to a source kind, a doc without anything of that kind drops out
    // and the others list and count only their places of that kind.
    if (this.placeSource && !bucket.sourceTypes.includes(this.placeSource))
      return [];
    const offline = this.offlineState(bucket.docId);
    if (offline) {
      return this.createOfflineDocItems(query, bucket, group, {
        title,
        icon,
        updatedDate,
        offline,
      });
    }
    const places = buildSearchDocPlaces(bucket, this.placeSource);
    const listKey = docPlacesListKey(bucket.docId);
    const { visible, hidden } = splitForLoadMore(
      places,
      PLACES_PER_DOC,
      this.expandedLists.has(listKey)
    );
    const items: QuickSearchItem<'docs', DocsPayload>[] = [
      {
        id: `doc-group:${bucket.docId}:${query}`,
        source: 'docs',
        group,
        label: { title: preferHighlightedTitle(markedDocTitle(bucket), title) },
        score: bucket.score,
        icon,
        row: 'doc',
        timestamp: updatedDate,
        badge: places.length
          ? {
              i18nKey: 'com.streamlain.quicksearch.doc.places',
              options: { count: places.length },
            }
          : undefined,
        payload: {
          docId: bucket.docId,
          title,
          sourceTypes: bucket.sourceTypes,
        },
      },
      ...visible.map(place =>
        this.createPlaceItem(query, bucket.docId, title, place, group)
      ),
    ];
    if (hidden > 0) {
      items.push({
        id: `doc-places-more:${bucket.docId}:${query}`,
        source: 'docs',
        group,
        label: {
          i18nKey: 'com.streamlain.quicksearch.loadMore',
          options: { count: hidden },
        },
        score: 0,
        row: 'more',
        payload: {
          docId: bucket.docId,
          sourceTypes: searchDocPlaceSourceTypes(places.slice(visible.length)),
        },
        beforeSubmit: () => {
          this.toggleListExpand(listKey);
          return false;
        },
      });
    }
    return items;
  };

  private readonly createPlaceItem = (
    query: string,
    docId: string,
    docTitle: string,
    place: SearchDocPlace,
    group: DocsResultGroup
  ): QuickSearchItem<'docs', DocsPayload> => {
    if (place.kind === 'pdf-page') {
      const { page, attachmentIndex, attachmentCount } = place;
      return {
        id: `pdf-page:${docId}:${page.attachmentBlockId}:${page.pageIndex}:${query}`,
        source: 'docs',
        group,
        label: {
          title:
            attachmentCount > 1
              ? {
                  i18nKey: 'com.streamlain.quicksearch.pdf.attachmentPageTitle',
                  options: { index: attachmentIndex, page: page.pageIndex + 1 },
                }
              : {
                  i18nKey: 'com.streamlain.quicksearch.pdf.pageTitle',
                  options: { page: page.pageIndex + 1 },
                },
          subTitle: createVisibleQuerySnippet(page.bestHit.snippet, query),
        },
        score: place.score,
        visual: { kind: 'pdf' },
        row: 'place',
        badge: pdfHitBadge(
          page.bestHit,
          page.hitCount > 1
            ? {
                i18nKey: 'com.streamlain.quicksearch.pdf.pageHits',
                options: { count: page.hitCount },
              }
            : undefined
        ),
        payload: {
          kind: 'pdf-page',
          docId,
          attachmentBlockId: page.attachmentBlockId,
          pageIndex: page.pageIndex,
          query,
          blockContent: page.bestHit.snippet,
          rects: page.bestHit.rects,
        },
      };
    }

    const { source } = place;
    const visual = docsHitVisual(source);
    const visibleSnippet = createVisibleQuerySnippet(
      source.blockContent,
      query
    );
    return {
      id:
        'doc:' +
        source.sourceType +
        ':' +
        docId +
        (source.blockId ? ':' + source.blockId : '') +
        (source.elementId ? ':' + source.elementId : '') +
        (source.databaseId ? ':' + source.databaseId : '') +
        (source.databaseRowId ? ':' + source.databaseRowId : '') +
        (source.databaseColumnId ? ':' + source.databaseColumnId : '') +
        ':' +
        query,
      source: 'docs',
      group,
      label: {
        title: databasePlaceLabel(source) ?? {
          i18nKey: QUICK_SEARCH_HIT_KIND_LABEL[visual.kind],
        },
        subTitle: visibleSnippet,
      },
      score: place.score,
      visual,
      row: 'place',
      badge: source.databaseUnsearchedShardCount
        ? {
            i18nKey: 'com.streamlain.quicksearch.database.unsearched',
            options: { count: source.databaseUnsearchedShardCount },
          }
        : source.technicalSourceKind === 'mail_storage'
          ? { i18nKey: 'com.streamlain.quicksearch.technical.mailStorage' }
          : undefined,
      payload: {
        docId,
        title: docTitle,
        blockId: source.blockId,
        elementId: source.elementId,
        parentBlockId: source.parentBlockId,
        parentFlavour: source.parentFlavour,
        noteBlockId: source.noteBlockId,
        displayMode: source.displayMode,
        blockContent: visibleSnippet,
        sourceType: source.sourceType,
        sourceTypes: [source.sourceType],
        databaseId: source.databaseId,
        databaseRowId: source.databaseRowId,
        databaseRowShardDocId: source.databaseRowShardDocId,
        databaseColumnId: source.databaseColumnId,
        visibility: source.visibility,
        technicalSourceKind: source.technicalSourceKind,
      },
    };
  };

  private readonly resolveBlockStatusForList = (
    docId: string,
    blockId: string
  ) => {
    const loaded = this.docsService.loaded(docId);
    if (!loaded) return 'unknown';
    try {
      return loaded.doc.blockSuiteDoc.getBlock(blockId) ? 'exists' : 'missing';
    } finally {
      loaded.release();
    }
  };

  private cache: {
    query: string;
    docs: DocsSearchResult[];
    /** Similar local pages, listed under the exact ones. */
    similar: DocsSearchResult[];
    /** Server-only hits; listed when the footer button was pressed. */
    online: OnlineHits | null;
  } | null = null;
  /** Docs whose places are all listed ("Show N more" was chosen). */
  private readonly expandedLists = new Set<string>();
  /** The source kind the places are scoped to; null lists every place. */
  private placeSource: SearchSourceType | null = null;

  /** Scope the listed places to `source$`, e.g. the advanced search's source chip. */
  listsPlacesOf(source$: LiveData<SearchSourceType | null>) {
    const subscription = source$.subscribe(source => {
      if (source === this.placeSource) return;
      this.placeSource = source;
      this.publishItems();
    });
    this.disposables.push(() => subscription.unsubscribe());
  }

  private readonly resetExpansion = () => {
    this.expandedLists.clear();
  };

  private readonly publishItems = () => {
    if (!this.cache) {
      this.items$.next([]);
      return;
    }
    const { query, docs, similar, online } = this.cache;
    const items = this.createItems(query, docs, []);
    const similarItems = this.createItems(
      query,
      similar,
      [],
      this.similarGroup
    ).map(item => ({ ...item, id: `similar:${item.id}` }));
    // A listed page with a server row hit shows again below, so online rows
    // carry their own ids.
    const onlineItems =
      online && this.onlineState$.value.kind === 'shown'
        ? this.createItems(
            query,
            online.docs,
            online.pdfHits,
            this.onlineGroupOf(online)
          ).map(item => ({ ...item, id: `online:${item.id}` }))
        : [];
    this.items$.next([...items, ...similarItems, ...onlineItems]);
  };

  private readonly onlineGroupOf = (online: Pick<OnlineHits, 'similar'>) =>
    online.similar ? this.similarOnlineGroup : this.onlineGroup;

  /** Page rows the button adds: built the way showOnline lists them. */
  private readonly withOnlineCount = (
    query: string,
    hits: Omit<OnlineHits, 'count'>
  ): OnlineHits => {
    const group = this.onlineGroupOf(hits);
    const count = this.createItems(
      query,
      hits.docs,
      hits.pdfHits,
      group
    ).filter(item => item.row === 'doc' && item.group === group).length;
    return { ...hits, count };
  };

  /** Lists the server-only hits below the local ones; nothing above moves. */
  private readonly showOnline = () => {
    if (!this.cache?.online) return;
    this.onlineState$.next({ kind: 'shown' });
    this.publishItems();
  };

  private readonly toggleListExpand = (listKey: string) => {
    if (this.expandedLists.has(listKey)) this.expandedLists.delete(listKey);
    else this.expandedLists.add(listKey);
    this.publishItems();
  };

  private readonly withoutServerAnswer = (docs: DocsSearchResult[]) =>
    from(this.docsSearchService.mergeDatabaseSearch(docs, null)).pipe(
      map(({ local }) => local)
    );

  /**
   * The local answer is the answer: it shows at once, offline the same as
   * online, and stays put. Online, the server is asked in the background
   * only for what the local answer lacks; the footer offers those hits.
   */
  private readonly searchDocs = (
    query: string
  ): Observable<
    | { kind: 'local'; docs: DocsSearchResult[] }
    | { kind: 'similar'; docs: DocsSearchResult[] }
    | { kind: 'online'; online: OnlineHits | null }
  > => {
    const prefer = this.resolveDocsSearchPrefer();
    this.modeStatus$.next(this.statusForSearchMode(prefer));
    const localPrefer = prefer === 'remote' ? 'local' : prefer;
    const local$ = this.docsSearchService
      .search$(query, { prefer: localPrefer })
      .pipe(
        // Cloud row hits need their shard to open. Name databases with
        // missing rows only while the server is unavailable; online, the
        // footer offers the server's additional hits for this query.
        switchMap(docs => {
          if (!this.isCloudWorkspace) return of(docs);
          const localDocs = filterIncompleteCloudDatabaseRowHits(docs);
          return isBackendAvailableForImmediateRequest()
            ? of(localDocs)
            : this.withoutServerAnswer(localDocs);
        }),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    // Few exact pages: the pages similar to the query are listed below them.
    const similar$ = local$.pipe(
      switchMap(docs => {
        if (pageCount(docs) >= SIMILAR_FALLBACK_BELOW) return of([]);
        const listed = new Set(docs.map(doc => doc.docId));
        return this.docsSearchService
          .search$(query, { prefer: localPrefer, similar: true })
          .pipe(map(similar => similar.filter(doc => !listed.has(doc.docId))));
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );
    const answers$ = merge(
      local$.pipe(map(docs => ({ kind: 'local' as const, docs }))),
      similar$.pipe(map(docs => ({ kind: 'similar' as const, docs })))
    );
    if (!this.isCloudWorkspace || !this.isSupportServerIndexer())
      return answers$;
    return merge(
      answers$,
      this.retryOnline$.pipe(
        startWith(undefined),
        switchMap(() => {
          if (!isBackendAvailableForImmediateRequest()) return EMPTY;
          this.onlineState$.next({ kind: 'counting' });
          return this.onlineHits$(query, local$, similar$);
        }),
        map(online => ({ kind: 'online' as const, online }))
      )
    );
  };

  /**
   * Server hits beyond the listed local answer; null when the server failed.
   * Row shards are assigned by mergeDatabaseSearch, the one shard rule. When
   * the server finds nothing exact beyond it, it is asked for similar hits.
   */
  private onlineHits$(
    query: string,
    local$: Observable<DocsSearchResult[]>,
    similar$: Observable<DocsSearchResult[]>
  ): Observable<OnlineHits | null> {
    return combineLatest([
      this.docsSearchService.search$(query, {
        prefer: 'remote',
        // Partitions whose every page is answered locally cost the server
        // nothing.
        excludedPartitions:
          this.searchPartitionSync.searchSources()?.excludedPartitions,
      }),
      from(this.searchPdfPages(query, 'remote')).pipe(
        catchError(() => {
          this.pdfSearchFailedUntil =
            Date.now() + PDF_SEARCH_FAILURE_COOLDOWN_MS;
          return of([] as PdfPageSearchHit[]);
        })
      ),
    ]).pipe(
      // Keep local merging and counting outside the server-error boundary.
      catchError(() => of(null)),
      switchMap(response => {
        if (response === null) return of(null);
        const [remoteDocs, pdfHits] = response;
        return combineLatest([local$, similar$]).pipe(
          take(1),
          switchMap(([localDocs, similarDocs]) => {
            const listed = [...localDocs, ...similarDocs];
            return this.serverHitsBeyond$(localDocs, listed, remoteDocs).pipe(
              switchMap(remote => {
                const exact = this.withOnlineCount(query, {
                  ...onlineHitsBeyond(listed, remote, pdfHits),
                  similar: false,
                });
                return exact.count > 0
                  ? of(exact)
                  : this.similarOnline$(query, localDocs, listed);
              })
            );
          })
        );
      })
    );
  }

  /** The server hits the shard rule leaves to the server. */
  private serverHitsBeyond$(
    localDocs: DocsSearchResult[],
    listed: DocsSearchResult[],
    remoteDocs: DocsSearchResult[]
  ) {
    return from(
      this.docsSearchService.mergeDatabaseSearch(localDocs, remoteDocs)
    ).pipe(map(({ remote }) => onlineHitsBeyond(listed, remote, []).docs));
  }

  /**
   * Similar server hits on content. Every partition is asked: the local
   * similar answer reads titles and file names only.
   */
  private similarOnline$(
    query: string,
    localDocs: DocsSearchResult[],
    listed: DocsSearchResult[]
  ): Observable<OnlineHits | null> {
    if (!isBackendAvailableForImmediateRequest()) return EMPTY;
    return this.docsSearchService
      .search$(query, { prefer: 'remote', similar: true })
      .pipe(
        catchError(() => of(null)),
        switchMap(remoteDocs =>
          remoteDocs === null
            ? of(null)
            : this.serverHitsBeyond$(localDocs, listed, remoteDocs).pipe(
                map(docs =>
                  this.withOnlineCount(query, {
                    docs,
                    pdfHits: [],
                    similar: true,
                  })
                )
              )
        )
      );
  }

  /** Where the online part of a new query starts. */
  private readonly initialOnlineState = (): OnlineSearchState => {
    if (!this.isCloudWorkspace || !this.isSupportServerIndexer())
      return { kind: 'none' };
    // Without the server the search waits for recovery; the footer names it
    // after the app's UI grace.
    return isBackendAvailableForImmediateRequest()
      ? { kind: 'counting' }
      : { kind: 'offline' };
  };

  private readonly queryEffect = effect(
    tap((query: string) => {
      const queryChanged = query !== this.lastQuery;
      this.lastQuery = query;
      const hasQuery = query.trim().length > 0;
      this.error$.next(null);
      this.cache = null;
      if (queryChanged) {
        this.resetExpansion();
      }
      this.items$.next([]);
      this.isQueryLoading$.next(hasQuery);
      this.isSettled$.next(!hasQuery);
      this.onlineState$.next({ kind: 'none' });
      if (!hasQuery) {
        this.modeStatus$.next(null);
      }
    }),
    debounceTime(DOCS_SEARCH_DEBOUNCE_MS),
    switchMap((query: string) => {
      const keyword = query.trim();
      if (!keyword) {
        this.modeStatus$.next(null);
        return EMPTY;
      }
      return this.searchDocs(keyword).pipe(
        tap(answer => {
          if (answer.kind === 'local') {
            this.cache = {
              query: keyword,
              docs: answer.docs,
              similar: this.cache?.similar ?? [],
              online: this.cache?.online ?? null,
            };
            this.publishItems();
            this.isSettled$.next(true);
            this.isQueryLoading$.next(false);
            return;
          }
          this.cache ??= {
            query: keyword,
            docs: [],
            similar: [],
            online: null,
          };
          if (answer.kind === 'similar') {
            this.cache.similar = answer.docs;
            this.publishItems();
            return;
          }
          this.cache.online = answer.online;
          if (this.onlineState$.value.kind === 'shown') {
            this.publishItems();
          } else {
            this.onlineState$.next(
              answer.online
                ? {
                    kind: 'ready',
                    count: answer.online.count,
                    similar: answer.online.similar,
                  }
                : { kind: 'unreachable' }
            );
          }
        }),
        onStart(() => {
          this.error$.next(null);
          this.cache = null;
          this.items$.next([]);
          this.isQueryLoading$.next(true);
          this.isSettled$.next(false);
          this.onlineState$.next(this.initialOnlineState());
        }),
        catchError(err => {
          this.error$.next(err instanceof Error ? err.message : err);
          this.cache = null;
          this.resetExpansion();
          this.items$.next([]);
          this.isQueryLoading$.next(false);
          this.isSettled$.next(true);
          this.onlineState$.next({ kind: 'none' });
          return EMPTY;
        }),
        onComplete(() => {})
      );
    })
  ) as Effect<string>;

  query(query: string) {
    this.queryEffect(query);
  }

  override dispose(): void {
    this.staleResultNudge.dispose();
    this.queryEffect.unsubscribe();
    this.retryOnline$.complete();
    super.dispose();
  }
}

function preferHighlightedTitle(
  searchTitle: string | undefined,
  displayTitle: string
) {
  return searchTitle?.includes('<b>') ? searchTitle : displayTitle;
}

/** A database cell names where it lies: the database, the row, the column. */
function databasePlaceLabel(source: DocsSearchResult) {
  const parts = [
    source.databaseTitle,
    source.databaseRowLabel,
    source.databaseColumnName,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : undefined;
}

function pdfHitBadge(
  hit: PdfPageSearchHit,
  fallback: I18nString | undefined
): I18nString | undefined {
  if (hit.technicalSourceKind === 'mail_storage') {
    return { i18nKey: 'com.streamlain.quicksearch.technical.mailStorage' };
  }
  return fallback;
}
